/**
 * Debug utilities for development
 */

// Tailwind breakpoints (should match tailwind.config.js)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

// Check if we're in development mode
export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development' || 
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1';

/**
 * Get the current Tailwind breakpoint based on window width
 * @returns {string} The current breakpoint (xs, sm, md, lg, xl, 2xl)
 */
export function getCurrentBreakpoint() {
  const width = window.innerWidth;
  
  if (width >= BREAKPOINTS['2xl']) {
    return '2xl';
  } else if (width >= BREAKPOINTS.xl) {
    return 'xl';
  } else if (width >= BREAKPOINTS.lg) {
    return 'lg';
  } else if (width >= BREAKPOINTS.md) {
    return 'md';
  } else if (width >= BREAKPOINTS.sm) {
    return 'sm';
  }
  
  return 'xs';
}

/**
 * Check if the current window width is at least the specified breakpoint
 * @param {string} breakpoint - The breakpoint to check (sm, md, lg, xl, 2xl)
 * @returns {boolean} True if the current width is at least the specified breakpoint
 */
export function isBreakpoint(breakpoint) {
  const width = window.innerWidth;
  return width >= (BREAKPOINTS[breakpoint] || 0);
}

/**
 * Update the debug indicator with the current breakpoint and window width
 */
export function updateDebugIndicator() {
  const debugIndicator = document.getElementById('debug-indicator');
  const breakpointLabel = document.getElementById('breakpoint-label');
  
  if (debugIndicator && breakpointLabel) {
    const width = window.innerWidth;
    const currentBreakpoint = getCurrentBreakpoint();
    
    // Remove all breakpoint classes
    debugIndicator.classList.remove(
      'breakpoint-sm', 
      'breakpoint-md', 
      'breakpoint-lg', 
      'breakpoint-xl', 
      'breakpoint-2xl'
    );
    
    // Update label and add appropriate class
    breakpointLabel.textContent = `${currentBreakpoint} (${width}px)`;
    debugIndicator.classList.add(`breakpoint-${currentBreakpoint}`);
  }
}

/**
 * Toggle the visibility of the debug indicator
 */
export function toggleDebugIndicator() {
  const debugIndicator = document.getElementById('debug-indicator');
  if (debugIndicator) {
    debugIndicator.classList.toggle('hidden');
    
    // Save preference to localStorage
    const isHidden = debugIndicator.classList.contains('hidden');
    localStorage.setItem('debugIndicatorHidden', isHidden);
  }
}

/**
 * Setup keyboard shortcut to toggle debug indicator (Alt+D)
 */
export function setupDebugKeyboardShortcut() {
  document.addEventListener('keydown', (event) => {
    // Alt+D to toggle debug indicator
    if (event.altKey && event.key === 'd') {
      toggleDebugIndicator();
    }
  });
}

/**
 * Initialize the debug indicator
 */
export function initDebugIndicator() {
  // Only show debug indicator in development mode or if forced by URL parameter
  const forceDebug = new URLSearchParams(window.location.search).has('debug');
  
  if (!IS_DEVELOPMENT && !forceDebug) {
    // Hide debug indicator in production
    const debugIndicator = document.getElementById('debug-indicator');
    if (debugIndicator) {
      debugIndicator.style.display = 'none';
    }
    return;
  }
  
  const debugIndicator = document.getElementById('debug-indicator');
  if (debugIndicator) {
    // Add click event to toggle visibility
    debugIndicator.addEventListener('click', toggleDebugIndicator);
    
    // Check localStorage for saved preference
    const isHidden = localStorage.getItem('debugIndicatorHidden') === 'true';
    if (isHidden) {
      debugIndicator.classList.add('hidden');
    }
    
    // Setup keyboard shortcut
    setupDebugKeyboardShortcut();
    
    // Initial update
    updateDebugIndicator();
    
    // Add resize listener
    window.addEventListener('resize', updateDebugIndicator);
  }
} 